import React from 'react';
import clsx from 'clsx';
import { Nullable, SelectOption } from 'global';
import { useForm, useWatch } from 'react-hook-form';
import { AttachMoney, Save } from '@mui/icons-material';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { AutocompleteField, CurrencyInputField, DateField, InputField, InterestInputField } from 'components/form';
import { DateFormats } from '../../../../constants/common';

export interface FinanceProgramMarkup {
  flat: Nullable<number>;
  percent: Nullable<number>;
}

export interface FundingCardFormValues {
  lenderName: string;
  financeProgram: string;
  dateFunded: Nullable<string>;
  fundedAmount: Nullable<number>;
  financeMarkupFlat: Nullable<number>;
  commissionReceivedDate: Nullable<string>;
  financeMarkupPercentage: Nullable<number>;
  financeCommissionFlat: Nullable<number>;
  financeCommissionPercent: Nullable<number>;
  fundedSubmission: Nullable<SelectOption & { dynamicCSId: string }>;
}

interface FundingCardLoadings {
  isFPMarkupLoading: boolean;
  isCSStatusUpdating: boolean;
  isFinanceProgramsLoading: boolean;
  isCreditSubmissionsLoading: boolean;
  isCreditSubmissionRevenueLoading: boolean;
}

export interface CreditSubmissionRevenue {
  lenderName: string;
  approvedAmount: Nullable<number>;
  fundedTimestamp: Nullable<string>;
  commissionReceivedDate: Nullable<string>;
  financeCommissionPercent: Nullable<number>;
}

interface FundingCardActions {
  getInitialFundingCardFormValues: () => Promise<FundingCardFormValues>;
  handleGetSubmissionRevenue: (dynamicsId: string) => Promise<Nullable<CreditSubmissionRevenue>>;
  onUpdateCreditSubmission: (
    id: string,
    dto: { commissionReceivedDate: Nullable<string>; financeCommissionPercent: Nullable<number> }
  ) => Promise<void>;
  handleFetchFinanceProgramMarkup: (financeProgramId: string) => Promise<Nullable<FinanceProgramMarkup>>;
}

export interface FundingCardProps extends FundingCardActions {
  loading: FundingCardLoadings;
  financeProgramsOptions: SelectOption[];
  fundedSubmissionOptions: Array<SelectOption & { dynamicCSId: string }>;
}

export const FundingCard = ({
  loading,
  financeProgramsOptions,
  fundedSubmissionOptions,
  onUpdateCreditSubmission,
  handleGetSubmissionRevenue,
  handleFetchFinanceProgramMarkup,
  getInitialFundingCardFormValues,
}: FundingCardProps) => {
  const {
    control,
    setValue,
    getValues,
    formState: { isDirty, defaultValues },
  } = useForm<FundingCardFormValues>({ defaultValues: getInitialFundingCardFormValues });

  const fundedSubmissionValue = useWatch({ control, name: 'fundedSubmission' });

  const handleUpdateCS = async (dynamicCSId: string) => {
    await onUpdateCreditSubmission(dynamicCSId, {
      commissionReceivedDate: getValues('commissionReceivedDate'),
      financeCommissionPercent: getValues('financeCommissionPercent'),
    });
  };

  const calcCommissionsFlat = () => {
    const { financeCommissionPercent, fundedAmount } = getValues();

    if (fundedAmount === null || financeCommissionPercent === null) return;

    const financeCommissionDollar = (fundedAmount * financeCommissionPercent) / 100;

    setValue(`financeCommissionFlat`, financeCommissionDollar);
  };

  const calcCommissionsPercent = () => {
    const { financeCommissionFlat, fundedAmount } = getValues();

    if (financeCommissionFlat === null || fundedAmount === null || fundedAmount === 0) return;

    const financeCommissionPercentage = (financeCommissionFlat / fundedAmount) * 100;

    setValue('financeCommissionPercent', +financeCommissionPercentage.toFixed(2));
  };

  const isSubmitBtnDisabled = !isDirty || !fundedSubmissionValue || loading.isCreditSubmissionRevenueLoading;

  return (
    <Box
      className={clsx(
        'revenueCard',
        (loading.isFPMarkupLoading || loading.isCreditSubmissionRevenueLoading) && 'revenueCard-loading'
      )}
    >
      <Box className="revenueCardHeader">
        <Box className="revenueCardHeaderIconWithTitle">
          <Box className="revenueCardHeaderIcon">
            <AttachMoney />
          </Box>

          <Typography variant="subtitle1" component="span">
            Finance
          </Typography>
        </Box>

        <Tooltip title={isSubmitBtnDisabled ? 'Select submission for save' : ''}>
          <span>
            <Button
              variant="contained"
              startIcon={<Save />}
              disabled={isSubmitBtnDisabled}
              loading={loading.isCSStatusUpdating}
              onClick={async () => {
                if (fundedSubmissionValue) await handleUpdateCS(fundedSubmissionValue.dynamicCSId);
              }}
            >
              Save
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Box>
        <Stack gap="1rem">
          <Stack direction="row" gap="1rem">
            <AutocompleteField
              control={control}
              name="fundedSubmission"
              options={fundedSubmissionOptions}
              isLoading={loading.isCreditSubmissionsLoading}
              textFieldProps={{ variant: 'outlined', label: 'Submission', size: 'small' }}
              extraAction={async nextValue => {
                if (nextValue?.value && defaultValues) {
                  const response = await handleGetSubmissionRevenue(nextValue.value);

                  if (!response) return;

                  setValue('lenderName', response.lenderName);
                  setValue('dateFunded', response.fundedTimestamp);
                  setValue('fundedAmount', response.approvedAmount);
                  setValue('commissionReceivedDate', response.commissionReceivedDate);
                  setValue('financeCommissionPercent', response.financeCommissionPercent);

                  if (response.approvedAmount && response.financeCommissionPercent) {
                    const financeCommissionFlat = (response.approvedAmount * response.financeCommissionPercent) / 100;
                    setValue('financeCommissionFlat', financeCommissionFlat);
                  } else {
                    setValue('financeCommissionFlat', null);
                  }
                }
              }}
            />
          </Stack>

          <Stack direction="row" gap="1rem">
            <InputField
              label="Lender"
              name="lenderName"
              control={control}
              inputProps={{ variant: 'outlined', disabled: true }}
            />

            <AutocompleteField
              control={control}
              name="financeProgram"
              options={financeProgramsOptions}
              isLoading={loading.isFinanceProgramsLoading}
              textFieldProps={{ variant: 'outlined', label: 'Finance Program', size: 'small' }}
              extraAction={async nextValue => {
                if (nextValue?.value) {
                  const response = await handleFetchFinanceProgramMarkup(nextValue.value);

                  if (!response) return;

                  setValue('financeMarkupFlat', response.flat);
                  setValue('financeMarkupPercentage', response.percent);
                }
              }}
            />
          </Stack>

          <Stack direction="row" gap="1rem">
            <Stack direction="row" gap="1rem" width="100%">
              <CurrencyInputField
                control={control}
                name="fundedAmount"
                label="Funded Amount"
                textFieldProps={{ variant: 'outlined', disabled: true }}
              />
            </Stack>

            <Stack direction="row" gap="1rem" width="100%">
              <CurrencyInputField
                control={control}
                label="Finance Markup ($)"
                name="financeMarkupFlat"
                textFieldProps={{ variant: 'outlined', disabled: true }}
              />
              <InterestInputField
                control={control}
                label="Finance Markup (%)"
                name="financeMarkupPercentage"
                textFieldProps={{ variant: 'outlined', disabled: true }}
              />
            </Stack>
          </Stack>

          <Stack direction="row" gap="1rem">
            <DateField
              disabled
              name="dateFunded"
              control={control}
              label="Date Funded"
              format={DateFormats.Short}
              slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
            />

            <DateField
              control={control}
              name="commissionReceivedDate"
              label="Commission Received Date"
              slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
            />
          </Stack>

          <Stack direction="row" gap="1rem">
            <InterestInputField
              control={control}
              label="Finance Commission (%)"
              name="financeCommissionPercent"
              textFieldProps={{ variant: 'outlined', disabled: !fundedSubmissionValue }}
              customOnChange={(nextValue, name) => {
                setValue(name, nextValue);
                calcCommissionsFlat();
              }}
            />
            <CurrencyInputField
              control={control}
              name="financeCommissionFlat"
              label="Finance Commission ($)"
              textFieldProps={{ variant: 'outlined', disabled: !fundedSubmissionValue }}
              customOnChange={(nextValue, name) => {
                setValue(name, nextValue);
                calcCommissionsPercent();
              }}
            />
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};
